// The users list as the URL describes it: search, type and status filters, pages of 25.
import { type Account, AccountStatus, AccountType } from "@cosmosai/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";
import type { UsersSearch } from "../context";

export const PAGE_SIZE = 25;

// Unknown or malformed values are dropped, not fatal.
const UsersSearchSchema = z.object({
  query: z
    .union([z.string(), z.number()])
    .transform(String)
    .pipe(z.string().trim().min(1).max(200))
    .optional()
    .catch(undefined),
  type: AccountType.optional().catch(undefined),
  status: AccountStatus.optional().catch(undefined),
  page: z.coerce.number().int().min(2).optional().catch(undefined),
});

/** The route's `validateSearch`: only the fields that are set, so URLs stay short. */
export const validateUsersSearch = (raw: Record<string, unknown>): UsersSearch =>
  Object.fromEntries(
    Object.entries(UsersSearchSchema.parse(raw)).filter(([, v]) => v !== undefined),
  ) as UsersSearch;

export interface AccountsView {
  /** The accounts of the current page. */
  rows: Account[];
  /** Accounts matching the search and filters. */
  shown: number;
  total: number;
  page: number;
  pages: number;
}

/** Lower case without accents: "Élodie" matches "elodie". */
const folded = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const compare = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

const byEmailThenNames = (a: Account, b: Account) =>
  compare(a.email, b.email) ||
  compare(a.firstName ?? "", b.firstName ?? "") ||
  compare(a.lastName ?? "", b.lastName ?? "");

/** Filters, sorts and pages the accounts; a page past the end shows the last one. */
export function viewOf(accounts: Account[], search: UsersSearch): AccountsView {
  const query = search.query ? folded(search.query) : "";
  const matching = accounts
    .filter((account) => {
      if (search.type && account.type !== search.type) return false;
      if (search.status && account.status !== search.status) return false;
      if (!query) return true;
      const names = [account.firstName, account.lastName].filter(Boolean).join(" ");
      return [account.email, names].some((text) => folded(text).includes(query));
    })
    .sort(byEmailThenNames);
  const pages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
  const page = Math.min(search.page ?? 1, pages);
  return {
    rows: matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    shown: matching.length,
    total: accounts.length,
    page,
    pages,
  };
}

/** The URL's search and a setter that replaces the history entry. */
export function useUsersSearch() {
  const search = useSearch({ strict: false }) as UsersSearch;
  const navigate = useNavigate();
  // An undefined value removes the parameter; page 1 is the absent page.
  const set = useCallback(
    (patch: { [K in keyof UsersSearch]?: UsersSearch[K] | undefined }) =>
      void navigate({
        to: ".",
        search: (prev: object) => {
          const next = { ...prev, ...patch } as UsersSearch;
          return next.page === 1 ? { ...next, page: undefined } : next;
        },
        replace: true,
      } as never),
    [navigate],
  );
  return { search, set };
}
